import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from 'store';

import { MembershipPlan } from 'models/plans.types';

import { ProviderStateProps, SignUpProps } from './signup.types';

export const initialState: SignUpProps = {
  doctor: {
    bookedSlotId: '',
    displayName: '',
    email: '',
    id: '',
    profileImage: '',
    rating: 0
  },
  dynamicLink: '',
  expiredToken: '',
  firstAppointment: {
    _id: '',
    appointmentDescription: '',
    appointmentMethod: 'video',
    appointmentTime: {
      endTime: '',
      startTime: ''
    },
    appointmentTypeId: '',
    bookedSlotId: '',
    categoryDisplayName: '',
    doctorId: ''
  },
  membershipPlans: [],
  promoVisited: '',
  selectedPricePoint: undefined,
  signupOptions: {
    approxQueueTime: '',
    campaignId: '',
    flow: '',
    flowVersion: '',
    mockJoinedPatients: 0,
    payWithInsurance: null,
    plan: '',
    predefinedCategory: ''
  },
  statesWhereAshOptionAvailable: [],
  user: {
    _id: '',
    accessToken: '',
    dob: '',
    doctorId: '',
    doctorLicenseState: '',
    email: '',
    firstName: '',
    gender: '',
    hasSubscription: false,
    initialLoginVideo: '',
    isAsapOptionAvailable: false,
    isInitialLogin: true,
    isUnlimitedPlan: false,
    lastName: '',
    mailingAddress: {
      address: '',
      city: '',
      state: '',
      zipCode: ''
    },
    nickName: '',
    password: '',
    phoneNumber: '',
    planCode: '',
    planId: '',
    preferredPharmacyID: 0,
    pricePointId: '',
    refreshToken: '',
    sexAtBirth: '',
    status: '',
    timezone: ''
  }
};

const singUpSlice = createSlice({
  initialState,
  name: 'signUp',
  reducers: {
    clearFirstAppointment: (state) => {
      state.firstAppointment = initialState.firstAppointment;
    },
    clearProviderState: (state) => {
      state.doctor = initialState.doctor;
    },
    clearUserState: (state) => {
      state.user = initialState.user;
      state.signupOptions = initialState.signupOptions;
    },
    setDynamicLink: (state, action: PayloadAction<SignUpProps['dynamicLink']>) => {
      state.dynamicLink = action.payload;
    },
    setExpiredToken: (state, action: PayloadAction<SignUpProps['expiredToken']>) => {
      state.expiredToken = action.payload;
    },
    setFirstAppointmentState: (
      state,
      action: PayloadAction<Partial<SignUpProps['firstAppointment']>>
    ) => {
      state.firstAppointment = { ...state.firstAppointment, ...action.payload };
    },
    setMembershipPlans: (state, action: PayloadAction<MembershipPlan[]>) => {
      state.membershipPlans = action.payload;
    },
    setPromoVisited: (state, action: PayloadAction<SignUpProps['promoVisited']>) => {
      state.promoVisited = action.payload;
    },
    setProviderState: (state, action: PayloadAction<Partial<ProviderStateProps>>) => {
      state.doctor = { ...state.doctor, ...action.payload };
    },
    setSelectedPricePoint: (state, action: PayloadAction<SignUpProps['selectedPricePoint']>) => {
      state.selectedPricePoint = action.payload;
    },
    setSignupOptions: (state, action: PayloadAction<Partial<SignUpProps['signupOptions']>>) => {
      state.signupOptions = { ...state.signupOptions, ...action.payload };
    },
    setStatesWhereAshIsAvailable: (state, action: PayloadAction<string[]>) => {
      state.statesWhereAshOptionAvailable = action.payload;
    },
    setUserState: (state, action: PayloadAction<Partial<SignUpProps['user']>>) => {
      state.user = { ...state.user, ...action.payload };
    }
  }
});

export const {
  clearFirstAppointment,
  clearProviderState,
  clearUserState,
  setDynamicLink,
  setExpiredToken,
  setFirstAppointmentState,
  setMembershipPlans,
  setPromoVisited,
  setProviderState,
  setSelectedPricePoint,
  setSignupOptions,
  setStatesWhereAshIsAvailable,
  setUserState
} = singUpSlice.actions;

export const selectSignUp = (state: RootState) => state.signUp;
export const selectSelectedPricePoint = (state: RootState) => state.signUp.selectedPricePoint;
export const selectStatesWhereAshIsAvailable = (state: RootState) =>
  state.signUp.statesWhereAshOptionAvailable;

export default singUpSlice.reducer;
